import React, { useMemo, useState } from "react";
import {
  getDailyTasks,
  createDailyTask,
  updateDailyTask,
  reorderDailyTasks,
  archiveDailyTask,
  toggleDailyTask,
  resetDailyTasks,
} from "../../helpers/fakebackend_helper";
import { useCachedRequest } from "../../helpers/cachedFetch";

// Founder Daily Tasks (issue #98, phase 1). Founder-only: the endpoints are
// gated by super-admin-only permissions. A 403 (or the backend not being
// deployed yet) hides the card entirely; any OTHER failure keeps the card
// visible with an inline error + Retry — it must never silently vanish.
// Done-state auto-resets at 05:00 ET server-side (founder-day keying).

// The axios interceptor rejects with a message STRING, so permission errors
// are recognized by text, not status code.
function isForbiddenMessage(msg) {
  return String(msg || "").toLowerCase().includes("permission");
}

// "Label | https://url" per line <-> [{label, url}]
function parseLinks(text) {
  return text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const idx = l.indexOf("|");
      if (idx === -1) return { label: null, url: l.trim() };
      return { label: l.slice(0, idx).trim() || null, url: l.slice(idx + 1).trim() };
    });
}
function linksToText(links) {
  return (Array.isArray(links) ? links : [])
    .map((l) => (l.label ? `${l.label} | ${l.url}` : l.url))
    .join("\n");
}

function HistoryStrip({ days, doneDays, createdDay, taskDay }) {
  const done = useMemo(() => new Set(doneDays || []), [doneDays]);
  return (
    <div style={{ display: "flex", gap: 3, alignItems: "center" }} title="Last 30 days — green = done">
      {days.map((d) => {
        const preCreation = createdDay && d < createdDay;
        const isToday = d === taskDay;
        const bg = preCreation
          ? "transparent"
          : done.has(d)
            ? "var(--bl-success-soft, #2BD56F)"
            : "rgba(255,255,255,0.10)";
        return (
          <span
            key={d}
            title={`${d}${preCreation ? " (before task existed)" : done.has(d) ? " — done" : " — missed"}`}
            style={{
              width: 7,
              height: 14,
              borderRadius: 2,
              background: bg,
              border: isToday
                ? "1px solid var(--bl-accent-light, #7B7CFF)"
                : preCreation
                  ? "1px solid rgba(255,255,255,0.06)"
                  : "1px solid transparent",
              flexShrink: 0,
            }}
          />
        );
      })}
    </div>
  );
}

function TaskForm({ initial, onSave, onCancel, saving, cancelLabel = "Cancel" }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [note, setNote] = useState(initial?.note || "");
  const [linksText, setLinksText] = useState(linksToText(initial?.links));

  const input = {
    width: "100%",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid var(--bl-line, rgba(255,255,255,0.1))",
    borderRadius: 8,
    color: "#fff",
    padding: "0.5rem 0.7rem",
    fontSize: 13,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "0.75rem", background: "rgba(255,255,255,0.03)", borderRadius: 10 }}>
      <input style={input} placeholder="Task title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
      <input style={input} placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} />
      <textarea
        style={{ ...input, minHeight: 56, fontFamily: "var(--bl-font-mono)", fontSize: 12 }}
        placeholder={"Links, one per line (optional):\nLabel | https://example.com"}
        value={linksText}
        onChange={(e) => setLinksText(e.target.value)}
      />
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          className="bl-btn-primary"
          disabled={saving || !title.trim()}
          style={{ fontSize: 12, padding: "0.35rem 0.9rem", opacity: title.trim() ? 1 : 0.5 }}
          onClick={() => onSave({ title: title.trim(), note: note.trim() || null, links: linksText.trim() ? parseLinks(linksText) : null })}
        >
          {saving ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          style={{ fontSize: 12, padding: "0.35rem 0.9rem", background: "transparent", border: "1px solid var(--bl-line, rgba(255,255,255,0.1))", borderRadius: 8, color: "var(--bl-text-soft)", cursor: "pointer" }}
          onClick={onCancel}
        >
          {cancelLabel}
        </button>
      </div>
    </div>
  );
}

const DailyTasksCard = () => {
  const req = useCachedRequest({
    key: "daily-tasks",
    // Forbidden -> null (hide the card). Anything else -> error marker so the
    // card renders a Retry state instead of disappearing.
    fetcher: () =>
      getDailyTasks().catch((msg) =>
        isForbiddenMessage(msg) ? null : { __error: String(msg || "Failed to load") }
      ),
  });
  const data = req.data;

  const [manage, setManage] = useState(false);
  const [adding, setAdding] = useState(false);
  // Bumping this remounts the add form with empty fields after each save,
  // so several tasks can be added in one manage session.
  const [formKey, setFormKey] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState(null);
  // Optimistic done-state while the toggle round-trips.
  const [optimistic, setOptimistic] = useState({});

  // Founder-only: 403 (or endpoint not deployed) -> no card at all.
  if (!data) return null;

  if (data.__error || !Array.isArray(data.tasks)) {
    return (
      <div className="bl-glass" style={{ padding: "1.25rem", marginBottom: "1.5rem" }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
          <h3 style={{ margin: 0, fontFamily: "var(--bl-font-display)", fontSize: "1.05rem", color: "#fff", fontWeight: 600 }}>Daily Tasks</h3>
          <div style={{ display: "flex", gap: 6 }}>
            <button type="button" onClick={() => req.refetch()} style={{ fontSize: 12, background: "transparent", border: "1px solid var(--bl-line, rgba(255,255,255,0.1))", borderRadius: 8, color: "var(--bl-text-soft)", padding: "0.25rem 0.7rem", cursor: "pointer" }}>
              Retry
            </button>
            <button
              type="button"
              title="Drops and rebuilds the daily-task tables with the 3 seed tasks. Erases task history."
              onClick={async () => {
                if (!window.confirm("Reset Daily Tasks? This rebuilds the tables with the seed tasks and erases history.")) return;
                try {
                  await resetDailyTasks();
                } catch (e) {
                  window.alert(`Reset failed: ${String(e)}`); // surface it — a swallowed failure looks like "reset did nothing"
                }
                req.refetch();
              }}
              style={{ fontSize: 12, background: "rgba(255,93,108,0.10)", border: "1px solid rgba(255,93,108,0.3)", borderRadius: 8, color: "#ff8a98", padding: "0.25rem 0.7rem", cursor: "pointer" }}
            >
              Reset &amp; reseed
            </button>
          </div>
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: "#ff8a98" }}>
          Couldn't load daily tasks{data.__error ? ` — ${data.__error}` : ""}. Your checks are safe on the server.
        </div>
      </div>
    );
  }

  const { task_day: taskDay, days, tasks } = data;

  const doneCount = tasks.filter((t) => (optimistic[t.id] ?? t.done_today)).length;

  // Run a mutation, surface any failure inline, and refresh. The card itself
  // never unmounts on failure.
  const run = async (fn) => {
    setSaving(true);
    setActionError(null);
    try {
      await fn();
      await req.refetch();
    } catch (msg) {
      setActionError(String(msg || "Something went wrong"));
    }
    setSaving(false);
  };

  const onToggle = async (t) => {
    const next = !(optimistic[t.id] ?? t.done_today);
    setOptimistic((o) => ({ ...o, [t.id]: next }));
    setActionError(null);
    try {
      await toggleDailyTask(t.id, next);
      await req.refetch();
    } catch (msg) {
      setActionError(String(msg || "Couldn't save the check"));
    }
    setOptimistic((o) => { const { [t.id]: _drop, ...rest } = o; return rest; });
  };

  const move = (t, dir) => {
    const ids = tasks.map((x) => x.id);
    const i = ids.indexOf(t.id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    run(() => reorderDailyTasks(ids));
  };

  return (
    <div className="bl-glass" style={{ padding: "1.25rem", marginBottom: "1.5rem" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8, marginBottom: "0.9rem", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
          <h3 style={{ margin: 0, fontFamily: "var(--bl-font-display)", fontSize: "1.05rem", color: "#fff", fontWeight: 600 }}>Daily Tasks</h3>
          <span style={{ fontSize: 12, color: "var(--bl-text-soft)", fontVariantNumeric: "tabular-nums" }}>
            {doneCount}/{tasks.length} · resets 05:00 ET
          </span>
        </div>
        <button
          type="button"
          onClick={() => { setManage((v) => !v); setAdding(false); setEditingId(null); setActionError(null); }}
          style={{ fontSize: 12, background: "transparent", border: "1px solid var(--bl-line, rgba(255,255,255,0.1))", borderRadius: 8, color: manage ? "var(--bl-accent-light, #7B7CFF)" : "var(--bl-text-soft)", padding: "0.25rem 0.7rem", cursor: "pointer" }}
        >
          {manage ? "Done managing" : "Manage"}
        </button>
      </div>

      {actionError && (
        <div style={{ marginBottom: 8, fontSize: 12, color: "#ff8a98" }}>
          {actionError}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
        {tasks.map((t) => {
          const isDone = optimistic[t.id] ?? t.done_today;
          const links = Array.isArray(t.links) ? t.links : [];
          if (editingId === t.id) {
            return (
              <TaskForm
                key={t.id}
                initial={t}
                saving={saving}
                onCancel={() => setEditingId(null)}
                onSave={(payload) => run(async () => { await updateDailyTask(t.id, payload); setEditingId(null); })}
              />
            );
          }
          return (
            <div key={t.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "0.5rem 0.25rem", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
              <button
                type="button"
                onClick={() => onToggle(t)}
                title={isDone ? "Mark undone" : "Mark done"}
                style={{
                  width: 20, height: 20, borderRadius: 6, flexShrink: 0, marginTop: 1,
                  border: isDone ? "1px solid var(--bl-success-soft, #2BD56F)" : "1px solid rgba(255,255,255,0.25)",
                  background: isDone ? "rgba(0,205,82,0.18)" : "transparent",
                  color: "var(--bl-success-soft, #2BD56F)", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
                }}
              >
                {isDone ? <i className="ri-check-line" style={{ fontSize: 14 }} /> : null}
              </button>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 14, color: isDone ? "var(--bl-text-soft)" : "#fff", textDecoration: isDone ? "line-through" : "none" }}>
                    {t.title}
                  </span>
                  {t.streak > 1 && (
                    <span title={`${t.streak}-day streak`} style={{ fontSize: 11, color: "#ffd199", display: "inline-flex", alignItems: "center", gap: 2 }}>
                      <i className="ri-fire-line" />{t.streak}
                    </span>
                  )}
                </div>
                {t.note && <div style={{ fontSize: 12, color: "var(--bl-text-soft)", marginTop: 2 }}>{t.note}</div>}
                {links.length > 0 && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 5 }}>
                    {links.map((l, i) => (
                      <a
                        key={i}
                        href={l.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: 11, color: "var(--bl-accent-light, #7B7CFF)", background: "rgba(123,124,255,0.10)", border: "1px solid rgba(123,124,255,0.18)", borderRadius: 6, padding: "1px 7px", textDecoration: "none", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      >
                        {l.label || String(l.url || "").replace(/^https?:\/\//, "").slice(0, 40)}
                      </a>
                    ))}
                  </div>
                )}
                <div style={{ marginTop: 6 }}>
                  <HistoryStrip days={days} doneDays={t.done_days} createdDay={t.created_day} taskDay={taskDay} />
                </div>
              </div>

              {manage && (
                <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                  <button type="button" title="Move up" disabled={saving} onClick={() => move(t, -1)} style={btnIcon}><i className="ri-arrow-up-s-line" /></button>
                  <button type="button" title="Move down" disabled={saving} onClick={() => move(t, +1)} style={btnIcon}><i className="ri-arrow-down-s-line" /></button>
                  <button type="button" title="Edit" disabled={saving} onClick={() => { setEditingId(t.id); setAdding(false); }} style={btnIcon}><i className="ri-pencil-line" /></button>
                  <button
                    type="button"
                    title="Archive (keeps history)"
                    disabled={saving}
                    onClick={() => { if (window.confirm(`Archive "${t.title}"?`)) run(() => archiveDailyTask(t.id)); }}
                    style={{ ...btnIcon, color: "#ff8a98" }}
                  >
                    <i className="ri-archive-line" />
                  </button>
                </div>
              )}
            </div>
          );
        })}
        {tasks.length === 0 && (
          <div style={{ fontSize: 13, color: "var(--bl-text-soft)", padding: "0.5rem 0" }}>
            No tasks yet — hit Manage, then Add task.
          </div>
        )}
      </div>

      {manage && (
        <div style={{ marginTop: "0.75rem" }}>
          {adding ? (
            // key bump after each save clears the form but KEEPS it open, so
            // several tasks can be added back-to-back in one session.
            <TaskForm
              key={formKey}
              saving={saving}
              cancelLabel="Done adding"
              onCancel={() => setAdding(false)}
              onSave={(payload) =>
                run(async () => {
                  await createDailyTask(payload);
                  setFormKey((k) => k + 1);
                })
              }
            />
          ) : (
            <button
              type="button"
              onClick={() => { setAdding(true); setEditingId(null); }}
              style={{ fontSize: 12, background: "transparent", border: "1px dashed var(--bl-line, rgba(255,255,255,0.2))", borderRadius: 8, color: "var(--bl-text-soft)", padding: "0.4rem 0.9rem", cursor: "pointer" }}
            >
              <i className="ri-add-line" style={{ marginRight: 4 }} />Add task
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const btnIcon = {
  width: 26,
  height: 26,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(255,255,255,0.05)",
  border: "1px solid var(--bl-line, rgba(255,255,255,0.1))",
  borderRadius: 6,
  color: "var(--bl-text-soft)",
  cursor: "pointer",
  fontSize: 14,
  padding: 0,
};

export default DailyTasksCard;
